// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Renders the established one-row-per-document Contract Review table.
 *
 * Existing ORA, SLIC, and UM Ventures workspaces expose document identity,
 * upload provenance, and direct actions as separate columns. Keeping that
 * presentation isolated prevents connector source-group UX from changing the
 * workflow users already rely on in flat workspaces.
 */
import { Download, History, Trash2 } from 'lucide-react'

import { Button, Checkbox } from '@/components/ui'
import type { Agent } from '@/subsystems/agents'

import type { AgentRunHistory, WorkspaceAgentRuns } from '../hooks/useWorkspaceAgentRuns'
import type { DocumentListItemWithPipelines } from '../models'
import { agentAcceptsDocuments } from '../lib/agentRunRules'
import { LatestRuns, RunAgentsMenu } from './WorkspaceRunControls'

interface FlatWorkspaceDocumentRowsProps {
  documents: DocumentListItemWithPipelines[]
  selected: Set<string>
  agents: Agent[]
  runHistory: WorkspaceAgentRuns
  runningAgentIdsFor: (document: DocumentListItemWithPipelines) => Set<string>
  onToggleDocument: (documentId: string) => void
  onRun: (agentIds: string[], documentIds: string[]) => void
  onView: (document: DocumentListItemWithPipelines, run: AgentRunHistory, version: number) => void
  onDownload: (document: DocumentListItemWithPipelines) => void
  onManageVersions: (documentId: string) => void
  onDelete: (document: DocumentListItemWithPipelines) => void
}

export function FlatWorkspaceDocumentRows({
  documents,
  selected,
  agents,
  runHistory,
  runningAgentIdsFor,
  onToggleDocument,
  onRun,
  onView,
  onDownload,
  onManageVersions,
  onDelete,
}: FlatWorkspaceDocumentRowsProps) {
  return documents.map((document) => {
    const runningAgentIds = runningAgentIdsFor(document)
    const disabledAgentIds = new Set(runningAgentIds)
    for (const agent of agents) {
      if (!agentAcceptsDocuments(agent, [document])
        || (agent.pipelineId === 'version-diff' && document.latestVersion < 2)) {
        disabledAgentIds.add(agent.agentId)
      }
    }

    return (
      <tr key={document.documentId} className="border-t border-border" data-testid={`cr-doc-row-${document.documentId}`}>
        <td className="px-3 py-2">
          <Checkbox
            aria-label={`Select ${document.documentId}`}
            checked={selected.has(document.documentId)}
            onCheckedChange={() => onToggleDocument(document.documentId)}
          />
        </td>
        <td className="whitespace-nowrap px-3 py-2 font-mono text-xs text-muted-foreground">
          {document.documentId}
        </td>
        <td className="min-w-64 max-w-80 px-3 py-2">
          <div>{document.latestFilename ?? document.name}</div>
          <div className="text-xs text-muted-foreground">v{document.latestVersion}</div>
        </td>
        <td className="whitespace-nowrap px-3 py-2">{document.uploadedBy ?? '—'}</td>
        <td className="whitespace-nowrap px-3 py-2">
          {document.latestVersionAt ? new Date(document.latestVersionAt).toLocaleString() : '—'}
        </td>
        <td className="px-3 py-2" data-testid={`cr-latest-run-${document.documentId}`}>
          <LatestRuns
            runs={runHistory.get(document.documentId) ?? []}
            runningAgentIds={runningAgentIds}
            onView={(run, version) => onView(document, run, version)}
            onRetry={(run) => onRun([run.agentId], [document.documentId])}
          />
        </td>
        <td className="px-3 py-2">
          <div className="flex items-center justify-end gap-1" data-testid={`cr-run-actions-${document.documentId}`}>
            <RunAgentsMenu
              agents={agents}
              disabledAgentIds={disabledAgentIds}
              runningAgentIds={runningAgentIds}
              onRun={(agentIds) => onRun(agentIds, [document.documentId])}
            />
            <Button variant="ghost" size="icon" aria-label={`Download ${document.documentId}`} onClick={() => onDownload(document)}>
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" aria-label={`Manage versions for ${document.documentId}`} onClick={() => onManageVersions(document.documentId)}>
              <History className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700" aria-label={`Delete ${document.documentId}`} onClick={() => onDelete(document)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </td>
      </tr>
    )
  })
}
